import React, { useContext, useEffect, useState } from 'react';
import {
  Field,
  SitecoreContext,
  LinkFieldValue,
  RichText,
  ImageField,
} from '@sitecore-jss/sitecore-jss-react';
import { MdArrowBack } from 'react-icons/md';
import './index.scss';
import { isLoggedIn } from '../../utils/authHelper';
import BackToTopButton from '../BackToTopButton/BackToTopButton';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Button,
} from '@mui/material';
import { postAPICall } from '../../dataFetcher/axiosMethodCalls';
import { APIWebstoreContext } from '../../context/globalContext';
import ToastNotification from '../ToastNotification/ToastNotification';
import { UnifiedPaymentDataService } from '../../utils/UnifiedPaymentDataService';
import { FiDownload } from 'react-icons/fi';
import GroupInvoiceTemplate from './GroupInvoiceTemplate';
import { InvoiceLayout } from './GroupInvoiceTemplate';
import { getIdpToken } from '../../utils/authHelper';
import ProcessOrderValidationService from '../CheckoutPage/ProcessOrderValidationService';

export interface UnifiedPaymentPageProps {
  fields: {
    BackToICPRedirection: Field<LinkFieldValue>;
    BackToDashboardRedirection: Field<LinkFieldValue>;
    InvoiceNotice: Field<string>;
    CustomInvoiceNotice: Field<string>;
    ToastMessage: Field<string>;
    InvalidInvoiceMessage: Field<string>;
    InvoiceDescription: Field<string>;
    CorrespondenceToAddress: Field<string>;
    Logo: ImageField;
    Stamp: ImageField;
    CheckMailToAddress: Field<string>;
    WireTransferMailToAddress: Field<string>;
    FederalTaxInstructions: Field<string>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
  invoiceIdentifier?: string;
}

export interface InvoiceDetailsSection {
  groupNumber: string;
  groupInvoiceId: string;
  groupNumberInvoiceId: string;
  groupInvoiceDate: string; // ISO string date
  signature?: string;
  unpaidInvoices: UnpaidInvoice[];
}

export interface MailingAddress {
  BilltoCity: string;
  BilltoCountry: string;
  BilltoFax: string | null;
  BilltoLine1: string;
  BilltoLine2: string;
  BilltoName: string;
  BilltoPostalcode: string;
  BilltoStateorprovince: string;
  BilltoTelephone: string;
}

export interface GroupInvoiceAddressSyncResponseModel {
  MailingAddress: MailingAddress;
  OrganizationName: string;
  ICPID: string;
  Email: string;
}

export interface FetchGroupInvoiceAddressResponse {
  PoNumber: string;
  Status: string;
  Message: string | null;
  UCGroupInvoiceAddressSyncResponseModel: GroupInvoiceAddressSyncResponseModel;
}

export interface UnpaidInvoice {
  orderId: string;
  rikOrderId: string;
  totalAmount: number;
  name: string;
  products: {
    productId: string;
    productName: string;
    quantity: number;
    price: number;
    type: string | null;
  }[];
  error?: {
    orderId: string;
    errorCode: string;
    errorMessage: string;
  };
}

const UnifiedPaymentInvoiceSection = (props: UnifiedPaymentPageProps): React.JSX.Element => {
  const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetailsSection | null>(null);
  const { state, dispatch } = useContext(APIWebstoreContext);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState<Field<string>>();
  const [customToastMessage] = useState(props.fields.ToastMessage);

  const params = new URLSearchParams(window.location.search);
  const redirectUrl = params.get('RedirectUrl')
    ? decodeURIComponent(params.get('RedirectUrl')!)
    : null;

  function convertDate(date: string | null): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  }

  useEffect(() => {
    fetchInvoiceDetails();
  }, []);

  async function fetchInvoiceDetails() {
    try {
      const rawQuery = window.location.search;
      const params = new URLSearchParams(rawQuery);

      const payload = Object.fromEntries(params.entries()) as Record<string, any>;
      payload.UnpaidInvoices = payload.UnpaidInvoices?.split(',').map((s: string) => s.trim());

      const apiUrl = `${process.env.REACT_APP_CART_URL}/checkout/custominvoicedetails`;

      const response = await postAPICall(apiUrl, payload);
      const data = response?.data as InvoiceDetailsSection;

      if (data) {
        setInvoiceDetails(data);

        if (dispatch && props?.invoiceIdentifier) {
          dispatch({
            type: 'INVOICE_UPDATE',
            payload: {
              invoicesSummary: {
                [props.invoiceIdentifier]: {
                  totalAmount:
                    data.unpaidInvoices
                      ?.filter((invoice) => !invoice.error)
                      .reduce((sum, invoice) => sum + invoice.totalAmount, 0) ?? 0,
                  invoiceAmounts: {},
                },
              },
              invoiceCounts: {
                totalCount: data.unpaidInvoices.length,
                lobCounts: { [props?.invoiceIdentifier]: data.unpaidInvoices.length },
              },
              toggleInvoiceSummaryLoad: !state?.toggleInvoiceSummaryLoad,
              groupInvoiceId: {
                [props?.invoiceIdentifier]: data.groupInvoiceId,
              },
              groupNumberInvoiceId: {
                [props?.invoiceIdentifier]: data.groupNumberInvoiceId,
              },
              adminGroupNumber: data.groupNumber,
              hasInvalidInvoices: data.unpaidInvoices.some((invoice) => invoice.error),
            },
          });
        }

        UnifiedPaymentDataService.setPaymentInfo(
          props?.invoiceIdentifier ?? '',
          response.data,
          false
        );
      } else {
        console.log('No data received from middleware.');
      }
    } catch (error) {
      console.error('Error parsing query parameters or fetching invoice details:', error);

      showCustomToast(
        state?.sitecoreDictionaryData && state?.sitecoreDictionaryData.InvoicePaymentError
          ? `${state?.sitecoreDictionaryData.InvoicePaymentError}`
          : 'Invalid Invoice Details'
      );
    }
  }

  const showCustomToast = (message: string) => {
    customToastMessage.value = message;
    setToastMessage(customToastMessage);
    setShowToast(true);
  };

  const toastRemove = () => {
    setShowToast(false);
  };

  useEffect(() => {
    const hasInvalidInvoices = invoiceDetails?.unpaidInvoices.some((invoice) => invoice.error);

    if (hasInvalidInvoices) {
      showCustomToast(
        props?.fields?.InvalidInvoiceMessage?.value ||
          'Payment cannot be processed at this time for invoice(s) in the "Invalid Invoices" section below. Please click on the "Back to ICP" button to return to the Unpaid Invoices page in the ICP Portal to review or reject the affected invoice(s).'
      );
    }
  }, [invoiceDetails]);

  if (!isLoggedIn()) return <></>;

  const validInvoices = invoiceDetails?.unpaidInvoices.filter((invoice) => !invoice.error) || [];
  const invalidInvoices = invoiceDetails?.unpaidInvoices.filter((invoice) => invoice.error) || [];

  const downloadPdf = async () => {
    // check if the billing address is saved?
    if (!ProcessOrderValidationService.getIsBillingAddressSaved().isBillingSaved) {
      customToastMessage.value =
        state?.sitecoreDictionaryData?.DownloadInvoiceError ??
        'Please enter and save the billing details.';
      setToastMessage(customToastMessage);
      setShowToast(true);
      return;
    }

    var billInfo = ProcessOrderValidationService.getBillingInfo().billingInfo();

    // get mailing address
    var mailingAddress = await getMailingAddress();
    if (!mailingAddress) {
      customToastMessage.value =
        state?.sitecoreDictionaryData?.MissingMailingAddressError ?? 'Could not generate invoice.';
      setToastMessage(customToastMessage);
      setShowToast(true);
      return;
    }

    // condition check..
    if (typeof window !== 'undefined') {
      await document.fonts.ready;

      const html2pdf = require('html2pdf.js');

      // Convert HTML string → DOM element
      const content = document.createElement('div');

      content.style.margin = '0';
      content.style.padding = '0';
      content.style.boxSizing = 'border-box';

      const updatedContent = buildMultipageInvoiceHtml(
        content,
        GroupInvoiceTemplate,
        validInvoices,
        mailingAddress,
        billInfo
      );

      const options = {
        margin: 10,
        html2canvas: {
          scale: 2,
          useCORS: true,
        },

        jsPDF: {
          unit: 'pt',
          format: 'a4',
          orientation: 'portrait',
        },

        pagebreak: {
          mode: ['css', 'legacy'],
        },
      };

      html2pdf().set(options).from(updatedContent).save(`${invoiceDetails?.groupInvoiceId}.pdf`);
    }
  };

  const getMailingAddress = async () => {
    try {
      const idpToken = await getIdpToken();
      const response = await postAPICall(
        `${process.env.REACT_APP_ICP_URL}/Group/FetchGroupInvoiceAddress?poNumber=${invoiceDetails?.groupInvoiceId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${idpToken}`,
          },
        }
      );
      return response?.data ?? null;
    } catch {
      return null;
    }
  };

  const getUpdatedInvoiceTemplate = (
    template: string,
    mailingAddressResponse: any,
    billingAddress: any
  ) => {
    let updatedTemplate = template;

    const getLogoHtml = (img?: ImageField) =>
      img?.value?.src ? `<img src="${img.value.src}" class="api-icp-logo" />` : '';

    updatedTemplate = updatedTemplate.replace('{{logo}}', getLogoHtml(props.fields.Logo));

    const getStampHtml = (img?: ImageField) =>
      img?.value?.src ? `<img src="${img.value.src}" class="icp-seal" />` : '';

    updatedTemplate = updatedTemplate.replace('{{stamp}}', getStampHtml(props.fields.Stamp));

    updatedTemplate = updatedTemplate.replace(
      '{{InvoiceDescription}}',
      props.fields.InvoiceDescription?.value ?? ''
    );

    updatedTemplate = updatedTemplate.replace(
      '{{CorrespondenceToAddress}}',
      props.fields.CorrespondenceToAddress?.value ?? ''
    );

    updatedTemplate = updatedTemplate.replace(
      '{{CheckMailToAddress}}',
      props.fields.CheckMailToAddress?.value ?? ''
    );

    updatedTemplate = updatedTemplate.replace(
      '{{WireTransferMailToAddress}}',
      props.fields.WireTransferMailToAddress?.value ?? ''
    );

    updatedTemplate = updatedTemplate.replace(
      '{{FederalTaxInstructions}}',
      props.fields.FederalTaxInstructions?.value ?? ''
    );

    updatedTemplate = updatedTemplate.replace(
      '{{companyName}}',
      mailingAddressResponse?.UCGroupInvoiceAddressSyncResponseModel?.OrganizationName ?? ''
    );

    const mailingAddress =
      mailingAddressResponse?.UCGroupInvoiceAddressSyncResponseModel?.MailingAddress;

    updatedTemplate = updatedTemplate.replace('{{firstName}}', billingAddress?.firstName ?? '');
    updatedTemplate = updatedTemplate.replace('{{lastName}}', billingAddress?.lastName ?? '');
    updatedTemplate = updatedTemplate.replace('{{addressline1}}', billingAddress?.street ?? '');
    updatedTemplate = updatedTemplate.replace('{{city}}', billingAddress?.city ?? '');
    updatedTemplate = updatedTemplate.replace('{{province}}', billingAddress?.state ?? '');
    updatedTemplate = updatedTemplate.replace('{{postalCode}}', billingAddress?.postal ?? '');
    updatedTemplate = updatedTemplate.replace('{{country}}', billingAddress?.country ?? '');

    if (billingAddress?.unit) {
      updatedTemplate = updatedTemplate.replace('{{addressline2}}', billingAddress.unit);
    } else {
      updatedTemplate = updatedTemplate.replace(/<p class="fs-15">\s*{{addressline2}}\s*<\/p>/, '');
    }

    updatedTemplate = updatedTemplate.replace(
      '{{mailingfirstName}}',
      mailingAddress?.BilltoName ?? ''
    );

    updatedTemplate = updatedTemplate.replace('{{mailinglastName}}', '');
    updatedTemplate = updatedTemplate.replace(
      '{{mailingaddressline1}}',
      mailingAddress?.BilltoLine1 ?? ''
    );
    updatedTemplate = updatedTemplate.replace('{{mailingcity}}', mailingAddress?.BilltoCity ?? '');
    updatedTemplate = updatedTemplate.replace(
      '{{mailingprovince}}',
      mailingAddress?.BilltoStateorprovince ?? ''
    );
    updatedTemplate = updatedTemplate.replace(
      '{{mailingpostalCode}}',
      mailingAddress?.BilltoPostalcode ?? ''
    );
    updatedTemplate = updatedTemplate.replace(
      '{{mailingcountry}}',
      mailingAddress?.BilltoCountry ?? ''
    );

    if (mailingAddress?.BilltoLine2) {
      updatedTemplate = updatedTemplate.replace(
        '{{mailingaddressline2}}',
        mailingAddress.BilltoLine2
      );
    } else {
      updatedTemplate = updatedTemplate.replace(
        /<p class="fs-15">\s*{{mailingaddressline2}}\s*<\/p>/,
        ''
      );
    }

    updatedTemplate = updatedTemplate.replaceAll(
      '{{invoiceNumber}}',
      invoiceDetails?.groupInvoiceId ?? ''
    );
    updatedTemplate = updatedTemplate.replace('{{date}}', getFormattedDateEST());
    updatedTemplate = updatedTemplate.replace(
      '{{icpId}}',
      mailingAddressResponse?.UCGroupInvoiceAddressSyncResponseModel?.ICPID ?? ''
    );
    updatedTemplate = updatedTemplate.replace(
      '{{email}}',
      mailingAddressResponse?.UCGroupInvoiceAddressSyncResponseModel?.Email ?? ''
    );

    return updatedTemplate;
  };

  const getFormattedDateEST = () => {
    const today = new Date();

    const estDate = new Date(
      today.toLocaleString('en-US', {
        timeZone: 'America/New_York',
      })
    );

    const day = estDate.getDate();

    const month = estDate.toLocaleString('en-US', { month: 'long' });

    const year = estDate.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const getRowUnits = (invoice: any) => {
    const name = invoice?.name ?? '';

    const productName = invoice?.products?.[0]?.productName ?? '';

    const nameLines = Math.ceil(name.length / InvoiceLayout.MaxNameCharPerLine) || 1;

    const productLines =
      Math.ceil(productName.length / InvoiceLayout.MaxProductNameCharPerLine) || 1;

    return Math.max(nameLines, productLines);
  };

  const paginateInvoices = (validInvoices: any[]) => {
    const pages: any[] = [];

    let currentPage: any[] = [];

    let currentUnits = 0;

    validInvoices.forEach((invoice) => {
      const rowUnits = getRowUnits(invoice);

      if (currentUnits + rowUnits > InvoiceLayout.MaxUnitsWithoutSummary) {
        pages.push(currentPage);

        currentPage = [];

        currentUnits = 0;
      }

      currentPage.push(invoice);

      currentUnits += rowUnits;
    });

    if (currentPage.length > 0) {
      pages.push(currentPage);
    }

    return pages;
  };

  const buildMultipageInvoiceHtml = (
    root: HTMLElement,
    template: string,
    validInvoices: any[],
    mailingAddressResponse: any,
    billingAddress: any
  ) => {
    const pages = paginateInvoices(validInvoices);

    const totalUnits = getTotalUnits(validInvoices);

    const lastPageUnits = getTotalUnits(pages[pages.length - 1]);

    const grandTotal = validInvoices.reduce((sum, invoice) => {
      const price = invoice.totalAmount ?? 0;

      return sum + price;
    }, 0);

    const isSinglePage = totalUnits <= InvoiceLayout.MaxUnitsWithSummary;

    const needsSummarySeparatePage =
      !isSinglePage && lastPageUnits > InvoiceLayout.MaxUnitsWithSummary;

    const counter = { value: 0 };
    // build data pages
    pages.forEach((pageInvoices, index) => {
      let pageHtml = getUpdatedInvoiceTemplate(template, mailingAddressResponse, billingAddress);

      // insert rows for THIS page
      const rows = buildInvoiceTableRows(pageInvoices, counter);

      pageHtml = pageHtml.replace('{{tableRows}}', rows.rows);

      const isLastDataPage = index === pages.length - 1;

      // remove table header except first page
      if (index === 0) {
        // keep header, just remove markers
        pageHtml = pageHtml.replace(/\{thead\}/, '');

        pageHtml = pageHtml.replace(/\{\/thead\}/, '');
      } else {
        // remove entire header block
        pageHtml = pageHtml.replace(/\{thead\}[\s\S]*?\{\/thead\}/, '');
      }

      // remove summary block when:
      // not last page OR summary must be separate page
      if (!isLastDataPage || needsSummarySeparatePage) {
        pageHtml = pageHtml.replace(/\{orderSummary\}[\s\S]*?\{\/orderSummary\}/, '');
      } else {
        // keep summary block
        pageHtml = pageHtml.replace(/\{orderSummary\}/, '');
        pageHtml = pageHtml.replace(/\{\/orderSummary\}/, '');
        pageHtml = pageHtml.replaceAll('{{totalAmount}}', formatUSD(grandTotal));
      }

      const page = document.createElement('div');

      page.style.margin = '0';
      page.style.padding = '4.7rem';
      page.style.boxSizing = 'border-box';
      page.innerHTML = pageHtml;

      root.appendChild(page);
    });

    // separate summary page when needed
    if (needsSummarySeparatePage) {
      let summaryHtml = getUpdatedInvoiceTemplate(template, mailingAddressResponse, billingAddress);

      // remove table rows
      summaryHtml = summaryHtml.replace('{{tableRows}}', '');

      // remove markers but keep content
      summaryHtml = summaryHtml.replace(/\{orderSummary\}/, '');
      summaryHtml = summaryHtml.replace(/\{\/orderSummary\}/, '');
      summaryHtml = summaryHtml.replaceAll('{{totalAmount}}', formatUSD(grandTotal));
      summaryHtml = summaryHtml.replace(/\{thead\}[\s\S]*?\{\/thead\}/, '');

      const summaryPage = document.createElement('div');

      summaryPage.style.margin = '0';
      summaryPage.style.padding = '4.7rem';
      summaryPage.style.boxSizing = 'border-box';
      summaryPage.innerHTML = summaryHtml;
      root.appendChild(summaryPage);
    }

    return root;
  };

  const getTotalUnits = (invoices: any[]) => {
    return invoices.reduce((sum, invoice) => {
      return sum + getRowUnits(invoice);
    }, 0);
  };

  const buildInvoiceTableRows = (validInvoice: any[], counterRef: { value: number }) => {
    let totalAmount = 0;

    const rows = validInvoice
      .map((invoice) => {
        const product = invoice.products?.find((p: any) => p.type !== 'Surcharge');

        const price = invoice.totalAmount;

        totalAmount += price;
        counterRef.value++;
        return `
          <tr>

            <td>
              ${counterRef.value}
            </td>

            <td>
              ${invoice.name ?? ''}
            </td>

            <td>
              ${invoice.rikOrderId ?? ''}
            </td>

            <td>
              ${product?.productName ?? ''}
            </td>

            <td class="amount">
              ${formatUSD(price)}
            </td>

          </tr>
        `;
      })
      .join('');

    return {
      rows,
      totalAmount,
    };
  };

  const formatUSD = (amount: number) => {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    });
  };

  return (
    <div className="unifiedpaymentinvoicesection">
      <ToastNotification
        fields={{
          ToastMessage: toastMessage,
        }}
        showToast={showToast}
        removeToast={toastRemove}
        toastStatus="error"
      ></ToastNotification>
      <h1 className="invoicePageTitle">Invoice Payment</h1>

      {invoiceDetails && (
        <>
          <a
            href={
              invoiceDetails.groupInvoiceId.length > 0
                ? redirectUrl || props?.fields?.BackToICPRedirection?.value?.href
                : props?.fields?.BackToDashboardRedirection?.value?.href
            }
            target={
              invoiceDetails.groupInvoiceId.length > 0
                ? props?.fields?.BackToICPRedirection?.value?.target
                : props?.fields?.BackToDashboardRedirection?.value?.target
            }
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', width: '100%' }}
          >
            <Button color="secondary" variant="contained" className="backtoICPButton">
              <MdArrowBack style={{ marginRight: '8px', fontSize: '22px' }} />
              {invoiceDetails.groupInvoiceId.length > 0
                ? props?.fields?.BackToICPRedirection?.value?.text
                : props?.fields?.BackToDashboardRedirection?.value?.text}
            </Button>
          </a>

          {invoiceDetails.groupInvoiceId.length > 0 ? (
            <RichText field={props?.fields?.InvoiceNotice} />
          ) : (
            <RichText field={props?.fields?.CustomInvoiceNotice} />
          )}

          {validInvoices.length > 0 && (
            <div className="invoice-table-section">
              <Typography variant="h6" gutterBottom className="unpaidInvoiceTitle">
                Unpaid Invoices
              </Typography>

              <TableContainer component={Paper} className="invoiceTableContainer">
                <Typography variant="body1" gutterBottom>
                  <p className="header-details">
                    <span>
                      <strong>Group Invoice #:</strong> {invoiceDetails.groupInvoiceId}
                    </span>
                    <span>
                      <strong>Group Invoice Date:</strong>{' '}
                      {convertDate(invoiceDetails.groupInvoiceDate)}
                    </span>
                    <span className="downloadLink" onClick={downloadPdf}>
                      <a href="#">
                        <FiDownload />
                        Download
                      </a>
                    </span>
                  </p>
                </Typography>

                <div className="invoiceTableScrollWrapper">
                  <Table className="invoiceTable">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Invoice #</TableCell>
                        <TableCell style={{ width: '20%' }}>Order #</TableCell>
                        <TableCell style={{ width: '25%' }}>Program</TableCell>
                        <TableCell align="right">Total Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validInvoices.map((invoice, index) => (
                        <TableRow
                          key={invoice.rikOrderId}
                          className={index % 2 === 0 ? 'tableRowEven' : 'tableRowOdd'}
                        >
                          <TableCell>{invoice.name}</TableCell>
                          <TableCell className="textBold">
                            <div className="invoiceNumber">{invoice.orderId}</div>
                          </TableCell>
                          <TableCell style={{ width: '20%' }}>{invoice.rikOrderId}</TableCell>
                          <TableCell style={{ width: '25%' }}>
                            {invoice.products[0]?.productName}
                          </TableCell>
                          <TableCell align="right">${invoice.totalAmount.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="grandTotalContainer">
                  <Typography variant="body1" className="grandTotalAmount">
                    Grand Total Amount: $
                    {validInvoices.reduce((sum, inv) => sum + inv.totalAmount, 0).toFixed(2)}
                  </Typography>
                </div>
              </TableContainer>
            </div>
          )}

          {invalidInvoices.length > 0 && (
            <div className="invoice-table-section invalidInvoicesSection">
              <Typography variant="h6" gutterBottom className="unpaidInvoiceTitle">
                Invalid Invoices
              </Typography>

              {toastMessage?.value && <p className="invalidInvoicesAlert">{toastMessage.value}</p>}

              <TableContainer component={Paper} className="invoiceTableContainer">
                <div className="invoiceTableScrollWrapper">
                  <Table className="invoiceTable">
                    <TableHead>
                      <TableRow>
                        <TableCell>Invoice #</TableCell>
                        <TableCell>Reason</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invalidInvoices.map((invoice, index) => (
                        <TableRow
                          key={invoice.orderId}
                          className={index % 2 === 0 ? 'tableRowEven' : 'tableRowOdd'}
                        >
                          <TableCell>{invoice.orderId}</TableCell>
                          <TableCell className="invalidInvoiceCell">
                            {invoice.error?.errorCode &&
                            state?.sitecoreDictionaryData?.[invoice.error.errorCode]
                              ? state.sitecoreDictionaryData[invoice.error.errorCode]
                              : invoice.error?.errorMessage}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TableContainer>
            </div>
          )}
        </>
      )}

      <BackToTopButton />
    </div>
  );
};

export default UnifiedPaymentInvoiceSection;
